// File: components/DocumentCard.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FaUpload, FaTrash, FaEye, FaFilePdf, FaFileImage, FaFileWord, FaFile, FaTimes } from 'react-icons/fa';
import gsap from 'gsap';

// Unique CSS class names to prevent conflicts
const docModalStyles = `
.doc-card-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.doc-card-modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 450px;
  max-height: 85vh;
  overflow: hidden;
  margin: 20px;
}

.doc-card-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.doc-card-modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
}

.doc-card-close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.doc-card-close-btn:hover {
  background-color: #e5e7eb;
  color: #374151;
}

.doc-card-confirm-body {
  padding: 2rem 1.5rem;
}

.doc-card-confirm-body p {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.6;
  color: #4b5563;
  text-align: center;
}

.doc-card-form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.doc-card-btn-secondary {
  padding: 0.6rem 1.5rem;
  background-color: #6b7280;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.doc-card-btn-secondary:hover {
  background-color: #4b5563;
  transform: translateY(-1px);
}

.doc-card-btn-danger {
  padding: 0.6rem 1.5rem;
  background-color: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.doc-card-btn-danger:hover {
  background-color: #b91c1c;
  transform: translateY(-1px);
}

.doc-card-btn-danger:disabled {
  background-color: #dc2626;
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Ensure modal stays on top of everything */
.doc-card-modal-overlay * {
  box-sizing: border-box;
}
`;

const DocCardDeleteModal = ({ isOpen, onClose, onConfirm, itemName }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const tl = gsap.timeline();
    tl.fromTo(modalRef.current,
      { opacity: 0, scale: 0.8 },
      { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" }
    );

    return () => tl.kill();
  }, [isOpen]);

  const handleClose = () => {
    const tl = gsap.timeline();
    tl.to(modalRef.current, { opacity: 0, scale: 0.8, duration: 0.3 })
      .eventCallback("onComplete", onClose);
  };

  const handleConfirm = () => {
    onConfirm();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{docModalStyles}</style>
      <div className="doc-card-modal-overlay" ref={modalRef}>
        <div className="doc-card-modal-content">
          <div className="doc-card-modal-header">
            <h3>Confirm Deletion</h3>
            <button className="doc-card-close-btn" onClick={handleClose}>
              <FaTimes />
            </button>
          </div>
          
          <div className="doc-card-confirm-body">
            <p>Are you sure you want to delete <strong>{itemName}</strong>? This action cannot be undone.</p>
          </div>
          
          <div className="doc-card-form-actions">
            <button type="button" onClick={handleClose} className="doc-card-btn-secondary">
              Cancel
            </button>
            <button type="button" onClick={handleConfirm} className="doc-card-btn-danger">
              <FaTrash /> Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const DocumentCard = ({ doc, onUpload, onDelete, onView }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const getFileIcon = (documentType, fileName) => {
    if (fileName?.includes('.pdf')) return <FaFilePdf className="profile-file-icon profile-pdf" />;
    if (fileName?.match(/\.(jpg|jpeg|png|gif)$/i)) return <FaFileImage className="profile-file-icon profile-image" />;
    if (fileName?.match(/\.(doc|docx)$/i)) return <FaFileWord className="profile-file-icon profile-word" />;
    return <FaFile className="profile-file-icon profile-generic" />;
  };

  const handleUploadClick = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      console.error("document is not available (not in browser)");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        setIsUploading(true);
        try {
          await onUpload(doc.documentType, file);
        } finally {
          setIsUploading(false);
        }
      }
    };

    input.click();
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      console.log('🗑️ Starting delete for document:', doc.documentId);
      await onDelete(doc.documentId);
      console.log('✅ Delete completed successfully');
      setShowDeleteModal(false);
    } catch (error) {
      console.error('❌ Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleView = () => {
    console.log('👀 Viewing document:', {
      id: doc.documentId,
      type: doc.documentType,
      url: doc.url
    });
    onView(doc);
  };

  return (
    <div className="profile-document-card">
      <div className="profile-document-card-header">
        {getFileIcon(doc.documentType, doc.fileName)}
        <h4>{doc.documentType}</h4>
      </div>

      <div className="profile-document-card-body">
        {doc.url ? (
          <>
            <p className="profile-document-status profile-uploaded">✓ Document Available</p>
          </>
        ) : (
          <p className="profile-document-status profile-missing">No document uploaded</p>
        )}
      </div>

      <div className="profile-document-card-actions">
        {!doc.url ? (
          <button 
            className="profile-btn-upload"
            onClick={handleUploadClick}
            disabled={isUploading}
          >
            <FaUpload /> 
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        ) : (
          <>
            <button className="profile-btn-view" onClick={handleView}>
              <FaEye /> View
            </button>
            <button 
              className="profile-btn-delete"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <FaTrash /> 
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </>
        )}
      </div>

      <DocCardDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        itemName={`${doc.documentType} document`}
      />
    </div>
  );
};

export default DocumentCard;